(ns app.domain.target-time.model
  (:require #?(:cljs [core.front.date-util :as date-util])
            [app.date :as date]
            [app.domain.group.config.model :as config-model]
            [app.domain.group.model :as group-model]
            [schema-tools.core :as st]
            [schema.core :as s])
  #?(:clj (:import [java.time DayOfWeek ZonedDateTime ZoneId]
                   [java.util Date])))

(def target-time-unit-choice-days
  #{:target-time.unit/business-days
    :target-time.unit/calendar-days})

(def target-time-unit-choice-days+years
  (conj target-time-unit-choice-days :target-time.unit/years))

(def calculation-starting-day-choice
  #{:target-time.starting-day/current-day
    :target-time.starting-day/next-day})

(def calculation-starting-day-default :target-time.starting-day/next-day)

(defn resolve-calculation-starting-day [workspace]
  (or (config-model/resolve-value :group.config/target-times-calculation-starting-day workspace)
      calculation-starting-day-default))

(def business-day-end-time-default [23 59])

(defn resolve-business-day-end-time [workspace]
  (or (config-model/resolve-value :group.config/target-times-business-day-end-time workspace)
      business-day-end-time-default))

(s/defschema TargetTimeUnit (apply s/enum target-time-unit-choice-days+years))

(def state->fields {:target-time/waiting-for-complete {:value        :application/target-time-completed-value
                                                       :unit         :application/target-time-completed-unit
                                                       :start-date   [:application :application/submitted]
                                                       :manual-start :application/target-time-complete-start-ts
                                                       :manual-end   :application/target-time-complete-end-ts}
                    :target-time/waiting-for-decision {:value        :application/target-time-decided-value
                                                       :unit         :application/target-time-decided-unit
                                                       :start-date   [:application :application/submitted]
                                                       :manual-start :application/target-time-decision-start-ts
                                                       :manual-end   :application/target-time-decision-end-ts}
                    :target-time/waiting-for-appeal   {:value        :application/target-time-appealed-value
                                                       :unit         :application/target-time-appealed-unit
                                                       ;; TODO use latest decision date from WIN-2000 as start-date if available
                                                       :start-date   [:last-state-change-date]
                                                       :manual-start :application/target-time-appeal-start-ts
                                                       :manual-end   :application/target-time-appeal-end-ts}})

(s/defschema TargetTimeState (apply s/enum (keys state->fields)))
(s/defschema TargetTimeValue (apply s/enum (->> state->fields
                                                vals
                                                (map :value))))

(s/defschema TimeTuple [(s/one s/Int "hour") (s/one s/Int "minute")])

(defprotocol DayWithType
  (-day-types [this]
    "Resolve the types of this day to a set of `target-time-unit-choice`")
  (-within-blackout? [this blackout]
    "Check if this day is within the given blackout (public holiday) period"))

#?(:clj
   (extend-protocol DayWithType
     ZonedDateTime
     (-day-types [^ZonedDateTime this]
       (if (contains? #{DayOfWeek/SATURDAY DayOfWeek/SUNDAY} (.getDayOfWeek this))
         #{:target-time.unit/calendar-days}
         #{:target-time.unit/business-days
           :target-time.unit/calendar-days}))
     (-within-blackout? [^ZonedDateTime this {:blackout/keys [^long start-time ^long end-time]}]
       (< start-time (-> this .toInstant .toEpochMilli) end-time)))

   :cljs
   (extend-protocol DayWithType
     ;; Note that this also covers goog.date.DateTime due to inheritance
     goog.date.Date
     (-day-types [this]
       (if (contains? #{0 6} (.getDay this))
         #{:target-time.unit/calendar-days}
         #{:target-time.unit/business-days
           :target-time.unit/calendar-days}))
     (-within-blackout? [this {:blackout/keys [start-time end-time]}]
       (< start-time (.getTime this) end-time))))

(defn business-day?
  [date blackouts]
  (and (contains? (-day-types date) :target-time.unit/business-days)
       (not-any? #(-within-blackout? date %) blackouts)))

(defn- has-day-type-fn
  [day-type blackouts]
  (when-not day-type
    (let [msg "has-day-type-fn called without day-type, it would be constantly false"]
      #?(:clj  (throw (Exception. msg))
         :cljs (throw (js/Error msg)))))
  (if (= day-type :target-time.unit/business-days)
    #(business-day? % blackouts)
    #(contains? (-day-types %) day-type)))

(defn- blackouts-provided-if-business-days?
  [{:keys [unit blackouts]}]
  (or (not= unit :target-time.unit/business-days)
      (sequential? blackouts)))

(s/defschema CountDaysContext
  (s/constrained
    (st/open-schema
      (st/merge
        {:start-date                 s/Inst
         :end-date                   s/Inst
         :unit                       TargetTimeUnit
         (s/optional-key :blackouts) [s/Any]}
        #?(:clj {:timezone s/Str})))
    blackouts-provided-if-business-days?
    'blackouts-needed-for-business-days))

(defn inst->long [date]
  #?(:clj (.getTime ^Date date)
     :cljs (.getTime ^js date)))

(defn- preserve-submitted-start-date?
  [field-config manual-start fallback-start-date]
  (and manual-start
       fallback-start-date
       (= [:application :application/submitted] (:start-date field-config))
       (< (inst->long manual-start)
          (inst->long fallback-start-date))))

(defn resolve-start-date
  ([application field-config fallback-start-date]
   (resolve-start-date application
                       field-config
                       fallback-start-date
                       (config-model/resolve-boolean :group.config/target-time-manual-inputs?
                                                     (:entity/parent application))))
  ([application field-config fallback-start-date manual-inputs?]
   (let [manual-start           (get application (:manual-start field-config))
         use-manual-start-date? (and manual-start
                                     (or manual-inputs?
                                         (preserve-submitted-start-date? field-config
                                                                         manual-start
                                                                         fallback-start-date)))]
     (if use-manual-start-date?
       manual-start
       fallback-start-date))))

(defn get-time-in-timezone [timezone date]
  (when (and timezone date)
    (let [date-in-tz #?(:cljs (date-util/to-tz date timezone)
                        :clj (-> date .toInstant (ZonedDateTime/ofInstant (ZoneId/of timezone))))]
      #?(:cljs [(.getHours date-in-tz) (.getMinutes date-in-tz)]
         :clj  [(.getHour date-in-tz) (.getMinute date-in-tz)]))))

(defn after-business-hours? [[business-h business-m] [h m]]
  (when (and business-h business-m h m)
    (or (> h business-h)
        (and (= h business-h)
             (>= m business-m)))))

(defn- filter-blackouts [blackouts start-time]
  (->> blackouts
       ;; Change these into simple epoch timestamps
       (map #(-> (select-keys % [:blackout/start-time :blackout/end-time])
                 (update :blackout/start-time inst->long)
                 (update :blackout/end-time inst->long)))
       (sort-by :blackout/end-time)
       (drop-while #(-> % :blackout/end-time (< (inst->long start-time))))))

(defn today-or-next-business-day
  "Returns `due-date` when it is a business day, otherwise the next business day."
  [due-date timezone blackouts]
  (let [coerce     (date/coerce-timestamp-fn {:timezone timezone})
        uncoerce   (date/uncoerce-timestamp-fn)
        start-date (coerce due-date)
        blackouts  (filter-blackouts blackouts due-date)
        business?  #(business-day? % blackouts)]
    (-> (if (business? start-date)
          start-date
          (date/adjust-date start-date 1 business?))
        uncoerce)))

(s/defn count-due-days :- s/Int
  "Count the number of full days between the given `start-date` (exclusive)
  and `end-date` (exclusive) dates."
  [{:keys [start-date end-date unit blackouts] :as ctx} :- CountDaysContext]
  (let [coerce (date/coerce-timestamp-fn ctx)]
    (when (and start-date end-date unit) ;; would crash if unit is nil
      (date/count-days
        ;; We only consider full days between the start and end dates
        (date/adjust-date (coerce start-date) 1)
        (coerce end-date)
        (has-day-type-fn unit (filter-blackouts blackouts start-date))))))

(s/defschema AddDaysContext
  (s/constrained
    (st/open-schema
      (st/merge
        {:start-date                 s/Inst
         :days                       s/Int
         :unit                       TargetTimeUnit
         :business-day-end-time      TimeTuple
         :calculation-starting-day   (apply s/enum calculation-starting-day-choice)
         :timezone                   s/Str
         (s/optional-key :blackouts) [s/Any]}))
    blackouts-provided-if-business-days?
    'blackouts-needed-for-business-days))

(s/defn calculate-due-date :- s/Inst
  "Calculate the due date from a given starting date and the number of days or just use manual end date if provided."
  [{:keys [start-date end-date days unit blackouts timezone calculation-starting-day business-day-end-time] :as ctx} :- AddDaysContext]
  (let [coerce                  (date/coerce-timestamp-fn ctx)
        uncoerce                (date/uncoerce-timestamp-fn)
        starting-day-modifier   (if (= calculation-starting-day :target-time.starting-day/next-day) 1 0)
        business-hours-modifier (if (after-business-hours? business-day-end-time
                                                           (get-time-in-timezone timezone start-date))
                                  1 0)
        total-days              (+ days starting-day-modifier business-hours-modifier)]
    (or end-date
        (when unit ;; would crash if unit is nil
          (-> (date/adjust-date
                (coerce start-date)
                total-days
                (has-day-type-fn unit (filter-blackouts blackouts start-date)))
              uncoerce)))))

(defn has-state? [app state]
  (->> (get state->fields state)
       :value
       (get app)
       some?))

(s/defn application->state :- (s/maybe TargetTimeState)
  "Resolve the target time state of an application"
  [{state :application/target-time-state :as application}]
  (cond
    (nil? state)
    nil
    (has-state? application state)
    state
    ;; The time between an application being submitted and deemed complete is timed by both
    ;; the completion timer and the decision timer, so we use whichever one is available
    (and (= state :target-time/waiting-for-complete)
         (has-state? application :target-time/waiting-for-decision))
    :target-time/waiting-for-decision
    (and (config-model/resolve-boolean :group.config/target-time-manual-inputs? (:entity/parent application))
         (group-model/has-feature? (-> application :entity/parent :domain/authority-group)
                                   :group.feature/target-processing-times))
    state))

(s/defschema DetailsContext
  (st/open-schema
    {:application            s/Any
     :last-state-change-date s/Inst
     :blackouts              [s/Any]
     :timezone               s/Str}))

(s/defschema TargetTimeFields
  {:state                     TargetTimeState
   :unit                      TargetTimeUnit
   :value-attr                TargetTimeValue
   (s/optional-key :value)    s/Int
   :start-date                s/Inst
   (s/optional-key :end-date) s/Inst
   :business-day-end-time     TimeTuple
   :calculation-starting-day  (apply s/enum calculation-starting-day-choice)})

(s/defn ^:private ctx->target-time-fields :- (s/maybe TargetTimeFields)
  [{:keys [application] :as ctx} :- DetailsContext]
  (when-let [state (application->state application)]
    (when-let [field-config (state->fields state)]
      (let [value    (get application (:value field-config))
            end-date (get application (:manual-end field-config))]
        (merge {:state                    state
                :value-attr               (:value field-config)
                :unit                     (or (get application (:unit field-config))
                                              :target-time.unit/calendar-days)
                :start-date               (resolve-start-date application
                                                              field-config
                                                              (get-in ctx (:start-date field-config)))
                :business-day-end-time    (or (-> application :application/target-times-business-day-end-time)
                                              (resolve-business-day-end-time (:entity/parent application)))
                :calculation-starting-day (or (-> application :application/target-times-calculation-starting-day)
                                              (resolve-calculation-starting-day (:entity/parent application)))}
               (when value
                 {:value value})
               (when end-date
                 {:end-date end-date}))))))

(s/defschema TargetTimeDetails
  (st/open-schema
    (st/merge
      TargetTimeFields
      (st/optional-keys {:due-date s/Inst}))))

(s/defn target-time-details :- (s/maybe TargetTimeDetails)
  [ctx :- DetailsContext]
  (when-let [{:keys [unit value start-date end-date business-day-end-time calculation-starting-day] :as target-time}
             (ctx->target-time-fields ctx)]
    (merge target-time
           {:timezone (:timezone ctx)}
           (when value
             {:due-date (or end-date
                            (calculate-due-date (merge
                                                  {:start-date               start-date
                                                   :end-date                 end-date
                                                   :days                     value
                                                   :unit                     unit
                                                   :business-day-end-time    business-day-end-time
                                                   :calculation-starting-day calculation-starting-day}
                                                  (select-keys ctx [:timezone
                                                                    :blackouts]))))}))))

(defn until-due-text [{:keys [paused? days-left business-days?]}]
  (cond
    paused?
    :target-time/paused

    (some-> days-left zero?)
    :target-time/due-today

    (neg-int? days-left)
    :search/due-date-overdue

    (and (= 1 days-left) business-days?)
    :target-time/business-day-until-due-date

    (and (= 1 days-left) (not business-days?))
    :target-time/day-until-due-date

    business-days?
    :target-time/business-days-until-due-date

    :else
    :target-time/days-until-due-date))
